import React, { useState, useEffect } from 'react';
import { useProperty } from '../../context/PropertyContext';
import toast from 'react-hot-toast';

const ProjectForm = ({ project, onSuccess, onCancel }) => {
  const { addProject, updateProject, formatPrice } = useProperty();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    type: '',
    image: '',
    status: 'Planning',
    units: '',
    description: '',
    startDate: '',
    completionDate: '',
    totalValue: '',
    features: ['']
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        location: project.location || '',
        type: project.type || '',
        image: project.image || '',
        status: project.status || 'Planning',
        units: project.units || '',
        description: project.description || '',
        startDate: project.startDate || '',
        completionDate: project.completionDate || '',
        totalValue: project.totalValue || '',
        features: project.features && project.features.length > 0 ? project.features : ['']
      });
    }
  }, [project]);

  const projectTypes = [
    'Residential Villas',
    'High-Rise Apartments',
    'Commercial Complex',
    'Gated Community',
    'IT Complex',
    'Premium Villas',
    'Luxury Apartments',
    'Eco-Friendly Villas',
    'Mixed Development',
    'Retail Complex'
  ];

  const projectStatuses = [
    'Planning',
    'Ongoing',
    'Completed',
    'On Hold'
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Project title is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.type) {
      newErrors.type = 'Project type is required';
    }

    if (!formData.units.trim()) {
      newErrors.units = 'Units/Size is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.completionDate) {
      newErrors.completionDate = 'Completion date is required';
    }

    if (formData.startDate && formData.completionDate && 
        new Date(formData.startDate) >= new Date(formData.completionDate)) {
      newErrors.completionDate = 'Completion date must be after start date';
    }

    if (!formData.totalValue || formData.totalValue <= 0) {
      newErrors.totalValue = 'Total project value is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      const projectData = {
        ...formData,
        totalValue: parseInt(formData.totalValue),
        features: formData.features.filter(feature => feature.trim() !== '')
      };

      if (project) {
        updateProject(project.id, projectData);
        toast.success('Project updated successfully!');
      } else {
        addProject(projectData);
        toast.success('Project added successfully!');
      }

      onSuccess();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Error saving project:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="property-form">
      <form onSubmit={handleSubmit}>
        <div className="form-sections">
          {/* Basic Information */}
          <div className="form-section">
            <h3>
              <i className="fas fa-info-circle"></i>
              Basic Information
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="title">Project Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={errors.title ? 'error' : ''}
                  placeholder="Enter project title"
                />
                {errors.title && <span className="error-message">{errors.title}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="location">Location *</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className={errors.location ? 'error' : ''}
                  placeholder="Enter project location"
                />
                {errors.location && <span className="error-message">{errors.location}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="type">Project Type *</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={errors.type ? 'error' : ''}
                >
                  <option value="">Select project type</option>
                  {projectTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.type && <span className="error-message">{errors.type}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="status">Project Status *</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  {projectStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="units">Units/Size *</label>
                <input
                  type="text"
                  id="units"
                  name="units"
                  value={formData.units}
                  onChange={handleInputChange}
                  className={errors.units ? 'error' : ''}
                  placeholder="e.g., 25 Villas, 200K Sq Ft"
                />
                {errors.units && <span className="error-message">{errors.units}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="totalValue">Total Project Value (₹) *</label>
                <input
                  type="number"
                  id="totalValue"
                  name="totalValue"
                  value={formData.totalValue}
                  onChange={handleInputChange}
                  className={errors.totalValue ? 'error' : ''}
                  placeholder="Enter total project value"
                  min="0"
                />
                {errors.totalValue && <span className="error-message">{errors.totalValue}</span>}
                {formData.totalValue && (
                  <small className="form-hint">
                    Formatted: {formatPrice(parseInt(formData.totalValue) || 0)}
                  </small>
                )}
              </div>

              <div className="form-group full-width">
                <label htmlFor="image">Project Image URL</label>
                <input
                  type="url"
                  id="image"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  placeholder="Enter image URL (optional)"
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="description">Project Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={errors.description ? 'error' : ''}
                  placeholder="Describe the project in detail"
                  rows="4"
                />
                {errors.description && <span className="error-message">{errors.description}</span>}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="form-section">
            <h3>
              <i className="fas fa-calendar-alt"></i>
              Project Timeline
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={errors.startDate ? 'error' : ''}
                />
                {errors.startDate && <span className="error-message">{errors.startDate}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="completionDate">Completion Date *</label>
                <input
                  type="date"
                  id="completionDate"
                  name="completionDate"
                  value={formData.completionDate}
                  onChange={handleInputChange}
                  className={errors.completionDate ? 'error' : ''}
                />
                {errors.completionDate && <span className="error-message">{errors.completionDate}</span>}
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="form-section">
            <h3>
              <i className="fas fa-star"></i>
              Project Features
            </h3>
            <div className="dynamic-fields">
              {formData.features.map((feature, index) => (
                <div key={index} className="dynamic-field">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    placeholder={`Feature ${index + 1}`}
                  />
                  {formData.features.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeFeature(index)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-btn"
                onClick={addFeature}
              >
                <i className="fas fa-plus"></i>
                Add Feature
              </button>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            <i className="fas fa-times"></i>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {project ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                {project ? 'Update Project' : 'Add Project'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;